import { useState, useCallback, useRef } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../Keycloak/useAuthenticatedFetchWithWorkspace";
import { Store } from "../../types/Store";
import { useStoreCache } from "../../context/StoreCacheContext";

interface GetStoreByIdParams {
    entity_id: string | number;
}

const useGetStoreById = () => {
    const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
    const { getFromCache, addToCache } = useStoreCache();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState<Store | null>(null);
    const [notFound, setNotFound] = useState<{ entityId: string | number } | null>(null);
    const requestIdRef = useRef(0);

    const getStoreById = useCallback(async ({ entity_id }: GetStoreByIdParams): Promise<Store | null> => {
        if (!isReady) {
            setError("Workspace not ready");
            return null;
        }

        const cached = getFromCache(entity_id);
        if (cached) {
            setData(cached);
            setError("");
            setNotFound(null);
            return cached;
        }

        // Incrémenter le compteur pour identifier cette requête comme la dernière
        const currentRequestId = ++requestIdRef.current;

        try {
            setLoading(true);
            setError("");
            setData(null);
            setNotFound(null);

            const result: Store = await authFetch(`/getStore?entity_id=${String(entity_id)}`, { method: "GET" });

            // Ignorer la réponse si une requête plus récente a été lancée
            if (currentRequestId !== requestIdRef.current) return null;

            if (!result) {
                setNotFound({ entityId: entity_id });
                return null;
            }

            addToCache(entity_id, result);
            setData(result);
            return result;
        } catch (err: unknown) {
            if (currentRequestId !== requestIdRef.current) return null;
            if (err instanceof Error) {
                const msg = err.message.toLowerCase();
                const isNotFound = msg.includes("404") || msg.includes("not found") || msg.includes("no entity")
                    || msg.includes("aucun") || msg.includes("introuvable")
                    || (msg.includes("500") && msg.includes("internal server error"));
                if (isNotFound) {
                    setNotFound({ entityId: entity_id });
                } else {
                    setError(err.message);
                }
            } else {
                setError("An unknown error occurred");
            }
            return null;
        } finally {
            if (currentRequestId === requestIdRef.current) setLoading(false);
        }
    }, [authFetch, isReady, workspaceId]); // ← workspaceId ajouté pour refetch quand le workspace change

    return { getStoreById, loading, error, data, isReady, notFound, workspaceId };
};

export default useGetStoreById;
